import { Tooltip_detail as Tooltip } from "../helpers/Tooltip";
import Img from "../../../../icons/ErrorIcon.svg";
import { isAlphanumericAllSpecialCharacters, isAlphanumericWithPlusAmpersandEqualsPlusMinus } from "../../../../functions/validations";
import PropTypes from 'prop-types';


// ============================================================================
// CONSTANTS AND CONFIGURATION
// ============================================================================

export const INPUT_WIDTHS = {
  STANDARD: "275px",
  TUBESHEET: "512px"
};

export const CSS_CLASSES = {
  ROW_BASE: "border WpsInput-md",
  ROW_ERROR: "border WpsInput-md error",
  CELL_BASE: "border WPSSelect",
  CELL_ERROR: "border WPSSelect error",
  HEADER: "text-start thead-color",
  INPUT_ERROR: "error-input"
};

// ============================================================================
// UTILITY FUNCTIONS
// Used across multiple components for consistent behavior
// ============================================================================

export const getRowClassName = (hasError) => hasError ? CSS_CLASSES.ROW_ERROR : CSS_CLASSES.ROW_BASE;
export const getCellClassName = (hasError) => hasError ? CSS_CLASSES.CELL_ERROR : CSS_CLASSES.CELL_BASE;
export const getInputWidth = (isTubesheet) => isTubesheet ? INPUT_WIDTHS.TUBESHEET : INPUT_WIDTHS.STANDARD;
export const safeArrayAccess = (arr, defaultValue = ["", "", ""]) => Array.isArray(arr) ? arr : defaultValue;
export const isFieldDisabled = (value) => value === "" || value === undefined;

const handleInput = (e, validatorFn, fieldName, setError) => {

  if (validatorFn == null) validatorFn = isAlphanumericAllSpecialCharacters
  const inputError = validatorFn(e.target.value);
  setError((prevState) => ({
    ...prevState,
    [fieldName]: inputError
  }));
  return inputError.status;
};



// ============================================================================
// COMMON COMPONENTS
// Reusable UI components used by multiple grid components
// ============================================================================

// Grid header with error tooltip - Used by all grid components
export const GridHeader = ({ ColumnHeader, Columndimension, ColumnHeader2Dimension, status, message, mainheading, ColumnHeader1 }) => (
  <th className={CSS_CLASSES.HEADER} style={{ whiteSpace: "nowrap" }}>
    <div>
      {mainheading ? (
        <>
          <div>{mainheading}</div>
          <div className="d-flex flex-column pt-3" style={{ width: "135px" }}>
            <span className="mb-3">{Columndimension} {ColumnHeader}</span>
            <div className="d-flex justify-content-between">{ColumnHeader1}<span>{ColumnHeader2Dimension}</span></div>
          </div>
        </>
      ) : (
        <>
          <div className="headwidth" style={{ width: "135px" }}>{ColumnHeader}</div>
          <span>{Columndimension}</span>
        </>
      )}
      {status && (
        <Tooltip content={message}>
          <img src={Img} alt="Error" className="ms-1" />
        </Tooltip>
      )}
    </div>
  </th>
);

// Alias for backward compatibility - now uses unified Process_Grid_Input
export const SingleProccessInput = (props) => (
  <Process_Grid_Input {...props} />
);
// Triple input component - Used by WPS_Process_Grid_Input_3Filermetal
export const TripleInput = ({
  item,
  name,
  type,
  maxLength,
  readOnly,
  disabled,
  status,
  onChange,
  id,
  validatorFn,
  setError,
  onInput, // For backward compatibility with PQR components
  singleInputMode = false, // New prop to control single vs triple input
  width // Optional width for the input
}) => {
  const inputStyle = { marginRight: "10px" };
  const firstInputStyle = { ...inputStyle, marginLeft: "10px" };

  // If single input mode, show only first input but maintain array structure
  if (singleInputMode) {
    return (
      <div className="dInput">
        <input
          key={0}
          type={type}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={disabled}
          style={{ marginLeft: "10px", marginRight: "10px", width: width || "100%" }}
          className={status ? CSS_CLASSES.INPUT_ERROR : ""}
          name={`${name}${id}_1`}
          value={item[name]?.[0] ?? ""} // Always use index 0
          onChange={(e) => onChange(id, 0, name, e)} // Always update index 0
          onInput={onInput || ((e) => setError && validatorFn && handleInput(e, validatorFn, name, setError))}
        />
      </div>
    );
  }

  // Original triple input mode
  return (
    <div className="dInput">
      {[0, 1, 2].map(index => (
        <input
          key={index}
          type={type}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={disabled}
          style={index === 0 ? firstInputStyle : inputStyle}
          className={status ? CSS_CLASSES.INPUT_ERROR : ""}
          name={`${name}${id}_${index + 1}`}
          value={item[name]?.[index] ?? ""}
          onChange={(e) => onChange(id, index, name, e)}
          onInput={onInput || ((e) => setError && validatorFn && handleInput(e, validatorFn, name, setError))}
        />
      ))}
    </div>
  );
};

// Double input row for amperage ranges - Used by WPS_Process_Grid_Input_2_Amperes
export const DoubleInputRow = ({
  item,
  id,
  name,
  name1,
  fieldfiller,
  type,
  maxLength,
  readOnly,
  disabled,
  status,
  onInput,
  handleChange,

}) => {
  const fillerArr = safeArrayAccess(item[fieldfiller]);


  const disabledStates = fillerArr.map(isFieldDisabled);
  console.log("fieldfiller", disabledStates)

  const inputStyle = { marginRight: "10px" };
  const firstInputStyle = { ...inputStyle, marginLeft: "10px" };

  const renderInputRow = (fieldName) => (
    <div className="dInput">
      {[0, 1, 2].map(index => {
        const isDisabled = item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined;
        if (isDisabled && item[fieldName]?.[index]) item[fieldName][index] = "";
        return (
          <input
            key={index}
            type={type}
            maxLength={maxLength}
            readOnly={readOnly}
            style={index === 0 ? firstInputStyle : inputStyle}
            className={status ? CSS_CLASSES.INPUT_ERROR : ""}
            disabled={isDisabled}
            name={`${fieldName}${index + 1}`}
            value={item[fieldName][index]}
            onChange={(e) => handleChange(id, index, fieldName, e)}
            onInput={onInput}
          />
        );
      })}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {renderInputRow(name)}
      <div className="dInput" style={{ margin: "5px 0" }}>
        {/* <span style={{ marginRight: "10px", marginLeft: "10px", textAlign: "center", display: "inline-block", width: "75px" }}>
          {"|"}
        </span> */}
        {/* <span style={{ marginRight: "10px", textAlign: "center", display: "inline-block", width: "75px" }}>
          {"|"}
        </span>
        <span style={{ marginRight: "10px", textAlign: "center", display: "inline-block", width: "75px" }}>
          {"|"}
        </span> */}
      </div>
      {/* {renderInputRow(name1)} */}
    </div>
  );
};

// Triple input with filler field dependency - Used by WPS_Process_Grid_Input_3
export const TripleInputWithdDpendency = ({
  item,
  name,
  fieldfiller,
  type,
  maxLength,
  readOnly,
  disabled,
  status,
  handleChange,
  id,
  validatorFn,
  setError

}) => {
  const inputStyle = { marginRight: "10px" };
  const firstInputStyle = { ...inputStyle, marginLeft: "10px" };
  console.log("TripleInputWithdDpendency", setError, validatorFn)
  return (
    <div className="dInput">
      {[0, 1, 2].map((_, index) => {
        const isDisabled = item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined;
        if (isDisabled && item[name]?.[index]) item[name][index] = "";
        return (
          <input
            key={index}
            type={type}
            maxLength={maxLength}
            readOnly={readOnly}
            disabled={isDisabled}
            style={index === 0 ? firstInputStyle : inputStyle}
            className={status ? CSS_CLASSES.INPUT_ERROR : ""}
            name={`${name}${index + 1}`}
            value={item[name]?.[index] || ""}
            onChange={(e) => handleChange(id, index, name, e)}
            onInput={(e) => handleInput(e, validatorFn, name, setError)}
          />
        );
      })}
    </div>
  );
};

// Triple select with filler field dependency - Used by WPS_Process_Grid_Select_3
export const TripleSelectWithDependecny = ({
  item,
  name,
  fieldfiller,
  array,
  disabled,
  status,
  handleChange,
  id

}) => {
  const selectStyle = {
    width: "85px",
    height: "32px",
    borderRadius: "4px",
    marginRight: "10px"
  };

  const firstSelectStyle = { ...selectStyle, marginLeft: "10px" };

  return (
    <div>
      {[0, 1, 2].map((_, index) => {
        const isDisabled = item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined;
        if (isDisabled && item[name]?.[index]) item[name][index] = "";
        return (
          <select
            key={index}
            style={index === 0 ? firstSelectStyle : selectStyle}
            className={status ? CSS_CLASSES.INPUT_ERROR : ""}
            name={`${name}${index + 1}`}
            value={item[name]?.[index] || ""}
            disabled={isDisabled}
            onChange={(e) => { handleChange(id, index, name, e) }}
          >
            {array?.map((e, idx) => (
              <option key={idx} value={e.value}>
                {e.name}
              </option>
            ))}
          </select>
        );
      })}
    </div>
  );
};

// Double input component - Used by WPS_Grid_Input_2
export const DoubleInput = ({
  item,
  name,
  type,
  maxLength,
  readOnly,
  disabled,
  status,
  onInput,
  handleChange,
  id,
  isTubesheet = false,
  fieldfiller
}) => {
  const inputWidth = isTubesheet ? "250px" : "131px";
  const inputStyle = { marginRight: "10px", width: inputWidth };
  const firstInputStyle = { ...inputStyle, marginLeft: "10px" };

  return (
    <div className="dInput">
      {[0, 1].map(index => (
        <input

          type={type}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={fieldfiller ? (item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined) : disabled}
          style={index === 0 ? firstInputStyle : inputStyle}
          className={status ? CSS_CLASSES.INPUT_ERROR : ""}
          name={`${name}${index + 1}`}
          value={item[name]?.[index] || ""}
          onChange={(e) => handleChange(id, index, name, e)}
          onInput={onInput}
        />
      ))}
    </div>
  );
};



export const Process_Grid_Input = ({
  status,
  message,
  ColumnHeader,
  Columndimension,
  type,
  name,
  handleChangedetailsarray,
  setError,
  detailObj,
  maxLength,
  width,
  isDisabled,
  item,
  handleChange,
  id,
  validatorFn,
  isTubesheet,
  readOnly,
  disabled,
  onInput, // Custom onInput handler
  // New props for PQR filler metal pattern
  wps_detail, // Array of items for PQR pattern
  handleOnInput, // Custom validation function
  TypeofCheck, // Validation type parameter
  isFillerMetal // Flag to indicate filler metal pattern
}) => {
  Process_Grid_Input.propTypes = {
    status: PropTypes.string,
    message: PropTypes.string,
    ColumnHeader: PropTypes.string,
    Columndimension: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
    handleChangedetailsarray: PropTypes.func,
    detailObj: PropTypes.array,
    maxLength: PropTypes.number,
    width: PropTypes.string,
    isDisabled: PropTypes.bool
  }

  // Determine usage pattern
  const isArrayBased = item !== undefined;
  const isFillerMetalPattern = wps_detail !== undefined && isFillerMetal;

  // For PQR filler metal pattern (full row with multiple cells)
  if (isFillerMetalPattern) {
    const textwidth = width !== undefined ? width : "";
    return (
      <tr className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
        <th className="text-start thead-color">
          <div>
            <div className="fillermetalheader">{ColumnHeader}</div>
            <span>{Columndimension}</span>
            {status && (
              <Tooltip content={message}>
                <img src={Img} alt="Error" className="ms-1" />
              </Tooltip>
            )}
          </div>
        </th>
        {wps_detail.map((item, id) => {
          const disabled = wps_detail[id]?.PQR_Process === "";
          const val = item[name] ?? "";
          return (
            <td
              className={message ? "border WPSSelect error" : "border WPSSelect"}
              key={item?.PK || item?.SK || `${name}-${id}`}
            >
              <input
                className={status ? "error-input" : ""}
                type={type}
                style={{ width: textwidth, margin: "5px" }}
                name={name}
                value={val}
                disabled={disabled}
                maxLength={maxLength}
                onChange={(e) => {
                  handleChangedetailsarray(e, id);
                  const err = handleOnInput(e, id, name, TypeofCheck, wps_detail);
                  setError((prevState) => ({
                    ...prevState,
                    [name]: err,
                  }));
                }}
              />
            </td>
          );
        })}
      </tr>
    );
  }

  // For array-based usage, return only the cell (exact SingleProccessInput behavior)
  if (isArrayBased) {
    return (
      <td className={getCellClassName(status)}>
        <input
          style={{ width: getInputWidth(isTubesheet) }}
          className={status ? CSS_CLASSES.INPUT_ERROR : ""}
          type={type}
          name={name}
          value={item[name] || ""}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLength}
          onChange={(e) => handleChange(e, id)}
          onInput={(e) => handleInput(e, validatorFn, name, setError)}
        />
      </td>
    );
  }

  // For object-based usage, return full row (exact original Process_Grid_Input behavior)
  return (
    <tr className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
      <th className={"text-start thead-color"}>
        <div>
          <div className="headwidth">{ColumnHeader} </div>
          <span>{Columndimension}</span>
          {status && (
            <Tooltip content={message}>
              <img src={Img} alt="Error" className="ms-1" />
            </Tooltip>
          )}
        </div>
      </th>
      <td className={status ? "border WPSSelect error" : "border WPSSelect"}>
        <input
          className={status ? "error-input" : ""}
          type={type}
          name={name}
          value={detailObj?.[name]}
          style={{ width: width }}
          maxLength={maxLength}
          disabled={isDisabled}
          onChange={(e) => {
            handleChangedetailsarray(e);
          }}
          onInput={(e) => {
            let error = isAlphanumericWithPlusAmpersandEqualsPlusMinus(e.target.value);
            setError((prevState) => ({
              ...prevState,
              [name]: error,
            }));
          }}
        />
      </td>
    </tr>
  );
};

export const Process_Grid_Input_2 = ({
  status,
  message,
  detailObj,
  ColumnHeader,
  Columndimension,
  ColumnHeader2Dimension,
  type,
  name,
  setError,
  readOnly,
  mainheading,
  handleChangedetailsarray,
  maxLength,
  ColumnHeader1,
  name1,
  width,
  onInput, // Optional: Custom onInput for first field
  onInput1, // Optional: Custom onInput for second field
  // New props for PQR array-based pattern
  wps_detail, // Array of items for PQR pattern
  maxLength1, // Custom maxLength for first input
  maxLength2, // Custom maxLength for second input
  width1, // Custom width
  thstyle2, // Custom header styling
  onBlur, // Blur handler for first input
  onBlur1, // Blur handler for second input
  regexValidator1,
  regexValidator2
}) => {
  Process_Grid_Input_2.propTypes = {
    status: PropTypes.string,
    type: PropTypes.string,
    message: PropTypes.string,
    ColumnHeader: PropTypes.string,
    Columndimension: PropTypes.string,
    mainheading: PropTypes.string,
    ColumnHeader1: PropTypes.string,
    name: PropTypes.string,
    handleChangedetailsarray: PropTypes.func,
    detailObj: PropTypes.array,
    maxLength: PropTypes.number,
    readOnly: PropTypes.bool,
    name1: PropTypes.string,
    width1:PropTypes.string
  }

  // Determine if this is PQR array-based pattern
  const isPQRArrayPattern = wps_detail !== undefined;

  // For PQR array-based pattern (maps over wps_detail)
  if (isPQRArrayPattern) {
    const textwidth1 = width1 ? width1 : "";
    console.log("wpsdetailproces",wps_detail)
    return (
      <tr style={{ height: "100px" }} className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
        <th className="text-start thead-color">
          <div style={thstyle2}>
            <div>{mainheading}</div>
            <div className="d-flex flex-column pt-3" style={{ width: "70%" }}>
              <span className="mb-3">{Columndimension} {ColumnHeader}</span>
              <div className="d-flex justify-content-between">{ColumnHeader1}<span>{ColumnHeader2Dimension}</span></div>
            </div>
            {status && (
              <Tooltip content={message}>
                <img src={Img} alt="Error" className="ms-1" />
              </Tooltip>
            )}
          </div>
        </th>
        {wps_detail.filter(item => item && (item.WPSD_Process !== "" || item.PQR_Process !== "")).map((item, id) => {
          const disabled = item?.PQR_Process === "" || item?.WPSD_Process === "";
          return (
            <td
              className={message ? "border WPSSelect error" : "border WPSSelect"}
             
            >
              <div className="d-flex flex-column align-items-center">
                <input
                  type={type}
                  className={status ? "error-input mb-3 mt-2" : "mb-3 mt-2"}
                  name={name}
                  style={{ width: textwidth1 }}
                  value={item[name]}
                  disabled={disabled}
                  maxLength={maxLength1 || maxLength}
                  onChange={(e) => {
                    handleChangedetailsarray(e, id);
                  }}
                  onInput={onInput}
                  onBlur={onBlur}
                />
                <input
                  type={type}
                  className={status ? "error-input mt-2" : "mt-2"}
                  name={name1}
                  value={item[name1]}
                  disabled={disabled}
                  style={{ width: textwidth1 }}
                  maxLength={maxLength2 || maxLength}
                  onChange={(e) => {
                    handleChangedetailsarray(e, id);
                  }}
                  onInput={onInput1}
                  onBlur={onBlur1}
                />
              </div>
            </td>
          );
        })}
      </tr>
    );
  }

  // For original object-based pattern (single detailObj)
  return (
    <tr
      style={{ height: "100px" }}
      className={status ? "border WpsInput-md error" : "border WpsInput-md"}
    >
      <th
        className={"text-start thead-color"}
      >
        <div>
          <div>{mainheading}</div>
          <div className="d-flex flex-column pt-3" style={{ width: "100%" }}>
            <span className="mb-3">{Columndimension} {ColumnHeader}</span>
            <div className="d-flex justify-content-between">{ColumnHeader1}<span>{ColumnHeader2Dimension}</span></div>
          </div>
          {status && (
            <Tooltip content={message}>
              <img src={Img} alt="Error" className="ms-1" />
            </Tooltip>
          )}
        </div>
      </th>
      <td className={status ? "border WPSSelect error" : "border WPSSelect"}>
        <div className="d-flex flex-column align-items-center">
          <input
            type={type}
            className={status ? "error-input mb-3 mt-2" : "mb-3 mt-2"}
            name={name}
            style={{ width: width }}
            value={detailObj?.[name]}
            readOnly={readOnly}
            maxLength={maxLength}
            onChange={(e) => {
              handleChangedetailsarray(e);
            }}
            onInput={onInput || ((e) => {

              const error = regexValidator1 ? regexValidator1(e.target.value) : isAlphanumericAllSpecialCharacters(e.target.value);

              setError((prevState) => ({
                ...prevState,
                [name]: error,
              }));
            })}
          />
          <input
            type={type}
            className={status ? "error-input mt-2" : "mt-2"}
            name={name1}
            style={{ width: width }}
            value={detailObj?.[name1]}
            readOnly={readOnly}
            maxLength={maxLength}
            onChange={(e) => {
              handleChangedetailsarray(e);
            }}
            onInput={onInput1 || ((e) => {

              const error = regexValidator2 ? regexValidator2(e.target.value) : isAlphanumericAllSpecialCharacters(e.target.value);

              setError((prevState) => ({
                ...prevState,
                [name1]: error,
              }));
            })}
          />
        </div>
      </td>
    </tr>
  );
};

export const GTAW_Process_Textbox = ({
  status,
  message,
  ColumnHeader,
  Columndimension,
  width,
  name,
}) => {
  GTAW_Process_Textbox.propTypes = {
    status: PropTypes.string,
    message: PropTypes.string,
    ColumnHeader: PropTypes.string,
    Columndimension: PropTypes.string,
    width: PropTypes.string,
    name: PropTypes.string,
  }
  return (
    <tr className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
      <th
        className={"text-start thead-color"}
      >
        <div>
          {ColumnHeader}
          <span>{Columndimension}</span>
          {status && (
            <Tooltip content={message}>
              <img src={Img} alt="Error" className="ms-1" />
            </Tooltip>
          )}
        </div>
      </th>
      <td className={status ? "border WPSSelect error" : "border WPSSelect"}>
        <input
          style={{
            width: width,
            height: "30px",
            borderRadius: "3px",
          }}
          readOnly
          className={status ? "error-input" : ""}
          name={name}
          value="GTAW"
        />
      </td>
    </tr>
  );
};

export const handlechange = (
  element,
  handle_2Input_ChangeA,
  handle_2Input_ChangeB,
  name, id,
  valueIndex,
  value,
  handle_2Input_ChangeC) => {
  let key = element?.Value_A !== undefined;
  let keyC = element?.Value_C !== undefined;
  if (key)
    handle_2Input_ChangeA(
      name,
      id,
      valueIndex,
      value
    );
  else if (keyC && handle_2Input_ChangeC)
    handle_2Input_ChangeC(
      name,
      id,
      valueIndex,
      value
    );
  else
    handle_2Input_ChangeB(
      name,
      id,
      valueIndex,
      value
    );
}

export const handleSetError = (setError, name, error, inputerr, element) => {
  let key = element?.Value_A !== undefined;
  let keyC = element?.Value_C !== undefined;
  console.log("inputerr", inputerr, element, key);
  const hasThreeValues = error?.[name]?.length >= 3;
  if (key)
    setError((prevState) => ({
      ...prevState,
      [name]: [{
        Value_A: [inputerr]
      },
      {
        Value_B: [
          error?.[name]?.[1]?.Value_B?.[0]
        ]
      },
      ...(hasThreeValues ? [{
        Value_C: [
          error?.[name]?.[2]?.Value_C?.[0]
        ]
      }] : []),
      ],
    }));
  else if (keyC)
    setError((prevState) => ({
      ...prevState,
      [name]: [{
        Value_A: [
          error?.[name]?.[0]?.Value_A?.[0]
        ]
      },
      {
        Value_B: [
          error?.[name]?.[1]?.Value_B?.[0]
        ]
      },
      { Value_C: [inputerr] },
      ],
    }));
  else
    setError((prevState) => ({
      ...prevState,
      [name]: [{
        Value_A: [
          error?.[name]?.[0]?.Value_A?.[0]
        ]
      },
      { Value_B: [inputerr] },
      ...(hasThreeValues ? [{
        Value_C: [
          error?.[name]?.[2]?.Value_C?.[0]
        ]
      }] : []),
      ],
    }));
}

export const WPQ_PQR_Process_Grid_Input2_tube = ({
  status,
  status1,
  status2,
  message,
  ColumnHeader,
  Columndimension,
  name,
  setError,
  error,
  readOnly,
  message1,
  message2,
  detailObj,
  handle_2Input_ChangeA,
  handle_2Input_ChangeB,
  handle_2Input_ChangeC,
  maxLength,
  fillerMetalField,
  validatorFn,
}) => {
  WPQ_PQR_Process_Grid_Input2_tube.propTypes = {
    status: PropTypes.string,
    status1: PropTypes.string,
    status2: PropTypes.string,
    message1: PropTypes.string,
    message2: PropTypes.string,
    message: PropTypes.string,
    ColumnHeader: PropTypes.string,
    Columndimension: PropTypes.string,
    error: PropTypes.array,
    name: PropTypes.string,
    setError: PropTypes.func,
    handle_2Input_ChangeA: PropTypes.func,
    handle_2Input_ChangeB: PropTypes.func,
    handle_2Input_ChangeC: PropTypes.func,
    detailObj: PropTypes.array,
    maxLength: PropTypes.number,
    readOnly: PropTypes.bool,
    fillerMetalField: PropTypes.string,
    validatorFn: PropTypes.func,
  };

  const hasThreeBoxes = !!handle_2Input_ChangeC;

  const boxes = hasThreeBoxes
    ? [
        { key: "Value_A", handler: handle_2Input_ChangeA, id: 0 },
        { key: "Value_B", handler: handle_2Input_ChangeB, id: 1 },
        { key: "Value_C", handler: handle_2Input_ChangeC, id: 2 },
      ]
    : [
        { key: "Value_A", handler: handle_2Input_ChangeA, id: 0 },
        { key: "Value_B", handler: handle_2Input_ChangeB, id: 1 },
      ];

  const isFieldDisabledByFiller = (boxId, boxKey) => {
    if (!fillerMetalField || !detailObj?.[fillerMetalField]) return false;
    const fillerArr = detailObj[fillerMetalField];
    const fillerElement = fillerArr[boxId];
    if (!fillerElement) return true;
    const fillerValue = fillerElement[boxKey]?.[0] ?? "";
    return fillerValue === "" || fillerValue === undefined;
  };

  return (
    <tr className={"border WpsInput-md"}>
      <th className={"text-start thead-color"}>
        <div>
          <div className="headwidth">{ColumnHeader} </div>
          <span>{Columndimension}</span>
          {(status || status1 || status2) && (
            <Tooltip content={message || message1 || message2}>
              <img src={Img} alt="Error" className="ms-1" />
            </Tooltip>
          )}
        </div>
      </th>
      {boxes.map((box) => {
        const dataElement = detailObj?.[name]?.[box.id];
        const value = dataElement?.[box.key]?.[0] ?? "";
        const disabledByFiller = isFieldDisabledByFiller(box.id, box.key);
        const element = dataElement || { [box.key]: [""] };

        return (
          <td
            className={status ? "border WPSSelect error" : "border WPSSelect"}
            key={box.id}
          >
            <div>
              <input
                className={status ? "error-input" : ""}
                type="text"
                name={name}
                disabled={readOnly || disabledByFiller}
                readOnly={disabledByFiller}
                value={disabledByFiller ? "" : value ? value : ""}
                maxLength={maxLength}
                onChange={(e) => box.handler(name, box.id, 0, e.target.value)}
                onInput={(e) => {
                  let inputerr = (validatorFn || isAlphanumericWithPlusAmpersandEqualsPlusMinus)(
                    e.target.value
                  );
                  handleSetError(setError, name, error, inputerr, element);
                }}
              />
            </div>
          </td>
        );
      })}
    </tr>
  );
};